<template>
    <div :class="['file-card', {'marked-for-delete': file.delete}]" v-tooltip.over="tooltip">
        <div class="flex flex-vertical-middle" v-tooltip="showCreated ? 'Hinzugefügt am ' + parseDate(file.created) : ''">
            <i class="material-icons" :style="'color: rgb(' + (iconColor) + ')'" aria-hidden="true" role="img">{{icon}}</i>

            <div class="text-overflow full-width">
                <div v-if="hasLightbox" class="flex flex-vertical-middle">
                    <a :href="filePath" class="flex flex-vertical-middle" target="_blank" title="Bild im neuen Tab öffnen">
                        <i aria-hidden="true" class="material-icons md-18" role="img">
                            open_in_new
                        </i>
                    </a>

                    <a v-light-box :href="filePath + (forceDownload ? '?force-download=1' : '')" :title="linkTitle"
                        :x-light-box="lightboxId" class="file-card-link">
                        {{fileDescription}}
                    </a>
                </div>
                <a v-else :href="filePath + (forceDownload ? '?force-download=1' : '')" :title="linkTitle" class="file-card-link" target="_blank">
                    {{fileDescription}}
                </a>

                <slot v-if="!SubSlotIsEmpty" name="sub" :file="file" :size="currentFileSize"></slot>
                <div v-if="showFileSize">
                    <small class="text-grey font-size-12">
                        <template v-if="file.size">{{currentFileSize}}&nbsp;</template>
                        <template v-if="showCreated">({{parseDate(file.created, 'd.m.Y')}})</template>
                    </small>
                </div>
            </div>

            <span v-if="deletePath" v-interact="deleteFile" :title="'Datei ' + fileDescription + ' löschen'" class="icon-interact icon-danger delete-hover action-icon"
                role="button">
                <i aria-hidden="true" class="material-icons md-18" role="img">delete</i>
            </span>
            <template v-else-if="deletable">
                <input v-if="file.delete" type="hidden" :name="deleteName + '[]'" :value="file.id"/>
                <span v-interact="markAsDeleted" :class="'icon-interact pointer action-icon ' + (file.delete ? 'text-warning' : 'icon-danger delete-hover')"
                    :title="file.delete ? 'Datei ' + fileDescription + ' wiederherstellen' : 'Datei ' + fileDescription + ' löschen'" role="button">
                    <i aria-hidden="true" class="material-icons md-18" role="img">
                        {{file.delete ? 'undo' : 'delete'}}
                    </i>
                </span>
            </template>
            <a v-else :href="filePath + '?force-download=1'" :title="(currentFileIsImage ? 'Bild ' : 'Datei ') + fileDescription + ' herunterladen'" target="_blank">
                <i class="material-icons md-18 action-icon" :class="{'color-contrast-primary-light': file.size !== 0, 'text-danger': file.size === 0}" aria-hidden="true"
                    role="img">
                    {{file.size === 0 ? 'error' : 'file_download'}}
                </i>
            </a>
        </div>
    </div>

</template>

<script setup lang="ts">
    import useEmptySlots from '@/composables/emptySlotCheck';
    import {usePortuna} from '@/composables/portuna';
    import {parseDate} from '@/utils/portuna/dateHelper';
    import fileSize from '../filters/fileSize';
    import {fileDefinition, isImage, isInlineFile, UPLOAD_TYPE_IMAGE} from '@/library/file';
    import {computed, toRefs} from 'vue';

    const portuna = usePortuna();

    const emit = defineEmits<{
        (eventName: 'delete-file', file: FileOrImageUpload): void;
    }>();

    const props = withDefaults(
        defineProps<{
            file: FileOrImageUpload;
            descriptionKey?: keyof FileOrImageUpload;
            showCreated?: boolean;
            deletable?: boolean;
            deletePath?: string | boolean;
            showFileSize?: boolean;
            lightboxId?: string;
            deleteName?: string;
            forceDownloadLink?: boolean;
        }>(),
        {
            descriptionKey: 'description',
            showCreated: true,
            deletable: false,
            deletePath: '',
            showFileSize: true,
            lightboxId: '',
            deleteName: 'delete_files',
            forceDownloadLink: true,
        },
    );

    const {
              file,
              descriptionKey,
              showCreated,
              deletable,
              deletePath,
              showFileSize,
              lightboxId,
              deleteName,
              forceDownloadLink,
          } = toRefs(props);

    const slots = defineSlots<{
        sub(props: { file: FileOrImageUpload, size: string }): void,
    }>();

    const currentFileSize = computed(() =>
    {
        return fileSize(file.value.size);
    });

    const {targetSlotIsEmpty: SubSlotIsEmpty} = useEmptySlots('sub', {
        'sub': {
            file,
            size: currentFileSize.value,
        },
    });

    const fileDescription = computed(() =>
    {
        return file.value[descriptionKey.value] || file.value.filename;
    });

    const currentFileIsImage = computed(() =>
    {
        return file.value.type === UPLOAD_TYPE_IMAGE;
    });

    const hasLightbox = computed(() =>
    {
        return !!(lightboxId.value && currentFileIsImage.value);
    });

    const getImagePath = (largest = false) =>
    {
        let filePath = '';
        if (!file.value.paths)
        {
            return filePath;
        }

        let paths = ['preview', 'medium', 'large', 'huge'] as (keyof UploadPaths)[];
        if (largest)
        {
            paths = paths.reverse();
        }

        for (const path of paths)
        {
            if (path in file.value.paths)
            {
                filePath = file.value.paths[path as (keyof typeof file.value.paths)]!;

                break;
            }
        }

        return filePath;
    };

    const markAsDeleted = (): void =>
    {
        file.value.delete = file.value.delete ? 0 : 1;
    };

    const deleteFile = (): void =>
    {
        emit('delete-file', file.value);
    };

    const isImageOrInlineFile = computed(() =>
    {
        return (
            currentFileIsImage.value ||
            isImage(currentFileDefinition.value.type) || // file-upload image
            isInlineFile(currentFileDefinition.value.type)
        ); // file-upload inline-file
    });

    const smallestImagePath = computed(() =>
    {
        return getImagePath();
    });

    const largestImagePath = computed(() =>
    {
        return getImagePath(true);
    });

    const tooltip = computed(() =>
    {
        if (file.value.size === 0)
        {
            return 'Diese Datei verfügt über keinen Inhalt.';
        }

        if (currentFileIsImage.value)
        {
            return `<img style="max-width: 250px;" src="${smallestImagePath.value}" alt="Vorschaubild" />`;
        }

        return '';
    });

    const filePath = computed(() =>
    {
        if (!file.value.paths)
        {
            return '';
        }

        if (currentFileIsImage.value)
        {
            return largestImagePath.value;
        }

        return file.value.paths.data!;
    });

    const forceDownload = computed(() =>
    {
        return (
            forceDownloadLink.value &&
            !isImageOrInlineFile.value &&
            !deletePath.value &&
            !deletable.value &&
            0 !== file.value.size
        );
    });

    const currentFileDefinition = computed(() =>
    {
        return fileDefinition(filePath.value);
    });

    const icon = computed(() =>
    {
        return file.value.icon || currentFileDefinition.value.icon;
    });

    const iconColor = computed(() =>
    {
        return file.value.icon_color || currentFileDefinition.value.color;
    });

    const linkTitle = computed(() =>
    {
        return (
            (currentFileIsImage.value ? 'Bild ' : 'Datei ') +
            fileDescription.value +
            (forceDownload.value ? ' herunterladen' : ' in neuem Tab öffnen')
        );
    });

    defineExpose(Object.assign({}, portuna, {componentName: 'FileListCard'}));
</script>